import { useState } from 'react';
import { FileJson, FileText, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSeedFromExport } from '@/services/devPanel/hooks';
import {
  readSeedableExport,
  replayedFilenames,
  replaysVerification,
  type DebugExport,
} from '@/utils/debug/seedFromExport';

/**
 * What the picked export turned out to be. Deliberately a summary and the file handle, not the
 * parsed export — a few thousand cargo items would sit in component state until the tab closed,
 * and the request builder re-reads the handle anyway.
 */
interface LoadedExport {
  file: File;
  replays: string[];
  carriesVerification: boolean;
  origin: string;
}

async function loadExport(file: File): Promise<LoadedExport> {
  let snapshot: DebugExport;
  try {
    snapshot = JSON.parse(await file.text()) as DebugExport;
  } catch {
    throw new Error(`${file.name} is not valid JSON.`);
  }

  const seedable = readSeedableExport(snapshot);
  return {
    file,
    replays: replayedFilenames(seedable),
    carriesVerification: replaysVerification(seedable),
    origin: seedable.origin,
  };
}

export function SeedFromExport() {
  const [loaded, setLoaded] = useState<LoadedExport | null>(null);
  const [sourcePdf, setSourcePdf] = useState<File | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const seed = useSeedFromExport();

  const pickExport = async (file: File | undefined) => {
    if (!file) return;
    setReadError(null);
    try {
      setLoaded(await loadExport(file));
    } catch (error) {
      setLoaded(null);
      setReadError((error as Error).message);
    }
  };

  const run = () => {
    if (!loaded) return;
    seed.mutate(
      { exportFile: loaded.file, sourcePdf },
      // A full load, not a router navigation: the copy has to hydrate from the server the way the
      // reported transaction did, and a client-side route would carry this session's stores across.
      { onSuccess: (transactionId) => window.location.assign(`/${transactionId}`) }
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3">
        <div>
          <h3 className="text-xs font-semibold">Seed from a debug export</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Rebuilds the transaction a debug export came from, here, as a copy you own — including
            the curation and verification it had accumulated. That is normally where a reported
            defect lives, so a copy reproduces it while a fresh upload does not.
          </p>
        </div>

        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2.5 text-xs hover:border-primary/60">
          <FileJson size={14} className="shrink-0 text-primary" />
          <span className="min-w-0 flex-1 truncate">
            {loaded ? loaded.file.name : 'Pick a debug-state JSON…'}
          </span>
          <input
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => void pickExport(e.target.files?.[0])}
          />
        </label>

        {readError && <p className="text-xs text-destructive">{readError}</p>}

        {loaded && (
          <div className="flex flex-col gap-2 rounded-lg border border-border/50 px-3 py-2.5">
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">from</span>
              <span className="truncate font-mono">{loaded.origin}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {loaded.replays.length === 0 && (
                <Badge variant="secondary">extraction only</Badge>
              )}
              {loaded.replays.map((filename) => (
                <Badge key={filename} variant="secondary" className="font-mono">
                  {filename}
                </Badge>
              ))}
            </div>
            {!loaded.carriesVerification && (
              <p className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-foreground">
                No substance_validation.json — substance verdicts start empty, so a defect in how a
                stored verification is read will not reproduce. Re-export from a build that includes
                it.
              </p>
            )}
          </div>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <h4 className="text-xs font-semibold text-muted-foreground">Source document</h4>
        <p className="text-xs text-muted-foreground">
          Exports leave the PDF out — it is binary, and the most sensitive thing in the file. Without
          one the copy gets a blank page and the document panel shows nothing useful; everything else
          is the reported transaction.
        </p>
        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2.5 text-xs hover:border-primary/60">
          <FileText size={14} className="shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate">
            {sourcePdf ? sourcePdf.name : 'Optional — pick the original PDF…'}
          </span>
          <input
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => setSourcePdf(e.target.files?.[0] ?? null)}
          />
        </label>
      </section>

      <Button onClick={run} disabled={!loaded} loading={seed.isPending} className="gap-1.5">
        <ExternalLink size={14} />
        Seed and open
      </Button>

      <p className="text-xs text-muted-foreground">
        The copy is flagged as seeded, so it stays out of analytics and the dev delete accepts it.
        Exports from a deployed environment carry customer cargo data.
      </p>
    </div>
  );
}
